import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useCommon } from '@/contexts/common-context';
import { useOrganization } from '@/contexts/organization-context';
import { toast } from '@/hooks/use-toast';
import { approvalClient } from '@/lib/approval-client';
import type { DealerPriceCreateRequest, DealerPriceCurrent } from '@/types/approval';
import { APPLY_TYPES, contractPriceLabel, LinePreview, hasNoRealApprover, useLinePreview, OIL_SALE_TYPES, PRICE_APP_METHODS } from './approval-common';
import { orgsOfType, uniqueOils, useDealers } from './approval-search';
import dayjs from 'dayjs';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmitted: (requestId: number) => void;
}

/** 거래처 계약단가 승인요청 상신(B220). 적용일은 익일 이후를 기본값으로 둔다 (설계서 §7.7) */
export function DealerPriceDialog({ open, onOpenChange, onSubmitted }: Props) {
  const [orgCd, setOrgCd] = useState('');
  const [dealerCode, setDealerCode] = useState('');
  const [dealerName, setDealerName] = useState('');
  const [productCode, setProductCode] = useState('');
  const [oilSaleType, setOilSaleType] = useState('1');
  const [applyType, setApplyType] = useState('A');
  const [priceAppMethod, setPriceAppMethod] = useState('3');
  const [applyDate, setApplyDate] = useState(dayjs().add(1, 'day').format('YYYY-MM-DD'));
  const [current, setCurrent] = useState<DealerPriceCurrent | null>(null);
  const [currentFailed, setCurrentFailed] = useState(false);
  const [requestedPrice, setRequestedPrice] = useState('');
  const [requestReason, setRequestReason] = useState('');
  const [saving, setSaving] = useState(false);

  // 결재 SPA 는 메인 쉘 밖이라 조직 목록을 아무도 안 불러 준다
  const { organizations, loadOrganizations } = useOrganization();
  const { getOils } = useCommon();
  useEffect(() => {
    if (open && organizations.length === 0) loadOrganizations();
  }, [open, organizations.length, loadOrganizations]);
  const stores = useMemo(() => orgsOfType(organizations, 'STORE'), [organizations]);
  const oils = useMemo(() => uniqueOils(getOils(orgCd)), [getOils, orgCd]);
  // 거래처는 사업장 소속만 고를 수 있다 (B220 §3.1). 사업장 전이면 전체 매장으로 퍼지므로 잠가 둔다
  const dealers = useDealers(open && orgCd !== '', orgCd, stores);

  useEffect(() => {
    if (!open) return;
    setOrgCd('');
    setDealerCode('');
    setDealerName('');
    setProductCode('');
    setOilSaleType('1');
    setApplyType('A');
    setPriceAppMethod('3');
    setApplyDate(dayjs().add(1, 'day').format('YYYY-MM-DD'));
    setRequestedPrice('');
    setRequestReason('');
  }, [open]);

  /**
   * 현재 계약단가는 정본 geo_dg 에서 읽는다 - 요청자가 외워 적을 값이 아니다. 키가 바뀌면 이전 값부터 버린다
   * (응답 전에 상신하면 이전 거래처의 단가가 저장된다). 실패는 드러낸다: 결재자는 이 값으로 현재 계약과 요청을 비교한다.
   */
  useEffect(() => {
    setCurrent(null);
    setCurrentFailed(false);
    if (!orgCd || !dealerCode || !productCode) return;
    let cancelled = false;
    approvalClient
      .currentDealerPrice(orgCd, dealerCode, productCode, oilSaleType)
      .then(found => !cancelled && setCurrent(found))
      .catch(() => !cancelled && setCurrentFailed(true));
    return () => {
      cancelled = true;
    };
  }, [orgCd, dealerCode, productCode, oilSaleType]);

  // 어느 칸이 비었는지 그대로 알려준다 - "필수입니다" 한 줄이면 다 채웠다고 믿는 칸을 찾을 수가 없다
  const missing = (
    [
      ['사업장', orgCd],
      ['거래처', dealerCode],
      ['유종', productCode],
      ['요청단가', requestedPrice],
      ['승인요청사유', requestReason],
    ] as const
  )
    .filter(([, value]) => !value.trim())
    .map(([label]) => label);
  const ready = missing.length === 0;
  // 미리보기와 상신이 같은 본문을 보내야 같은 결재선이 나온다
  const body = (): DealerPriceCreateRequest => ({
    orgCd: orgCd.trim(),
    dealerCode: dealerCode.trim(),
    dealerName: dealerName.trim() || null,
    productCode: productCode.trim(),
    productName: oils.find(oil => oil.code === productCode)?.name ?? null,
    oilSaleType,
    oilSaleTypeName: OIL_SALE_TYPES.find(t => t.code === oilSaleType)?.name ?? null,
    applyDate,
    currentApplyType: current?.applyType ?? null,
    currentApplyTypeName: APPLY_TYPES.find(t => t.code === current?.applyType)?.name ?? null,
    applyType,
    applyTypeName: APPLY_TYPES.find(t => t.code === applyType)?.name ?? null,
    currentPriceAppMethod: current?.priceAppMethod ?? null,
    currentPriceAppMethodName: PRICE_APP_METHODS.find(t => t.code === current?.priceAppMethod)?.name ?? null,
    priceAppMethod,
    priceAppMethodName: PRICE_APP_METHODS.find(t => t.code === priceAppMethod)?.name ?? null,
    currentPrice: current?.price ?? null,
    requestedPrice: Number(requestedPrice),
    requestReason: requestReason.trim(),
  });

  const preview = useLinePreview('dealer-price', ready && !currentFailed ? body() : null);
  const blocked = currentFailed || hasNoRealApprover(preview.data);

  const handleSubmit = async () => {
    if (!ready) {
      toast({ title: '입력 확인', description: `비어 있는 항목: ${missing.join(', ')}`, variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const detail = await approvalClient.submitDealerPrice(body());
      toast({ title: '상신 완료', description: detail.request.documentNo });
      onOpenChange(false);
      onSubmitted(detail.request.id);
    } catch (error) {
      toast({
        title: '상신 실패',
        description: error instanceof Error ? error.message : '승인요청을 등록하지 못했습니다',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[600px]'>
        <DialogHeader>
          <DialogTitle>거래처 계약단가 승인요청</DialogTitle>
          <DialogDescription>
            최종승인되면 지정한 적용일에 맞춰 POS로 단가가 전달됩니다. 적용일이 지나면 취소할 수 없습니다.
          </DialogDescription>
        </DialogHeader>

        <div className='grid gap-3 py-2'>
          <div className='grid grid-cols-2 gap-3'>
            <div className='space-y-1'>
              <Label>사업장</Label>
              <Select
                value={orgCd}
                onValueChange={code => {
                  setOrgCd(code);
                  // 사업장을 바꾸면 이전 사업장의 거래처·유종이 남으면 안 된다(유종은 주유소마다 다른 상품일 수 있다)
                  setDealerCode('');
                  setDealerName('');
                  setProductCode('');
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder='사업장 선택' />
                </SelectTrigger>
                <SelectContent>
                  {stores.map(store => (
                    <SelectItem key={store.orgCd} value={store.orgCd!}>
                      {store.orgName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className='space-y-1'>
              <Label>거래처</Label>
              <Select
                value={dealerCode}
                disabled={orgCd === ''}
                onValueChange={code => {
                  setDealerCode(code);
                  setDealerName(dealers.find(dealer => dealer.code === code)?.name ?? '');
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={orgCd === '' ? '사업장을 먼저 고르세요' : '거래처 선택'} />
                </SelectTrigger>
                <SelectContent>
                  {dealers.map(dealer => (
                    <SelectItem key={dealer.code} value={dealer.code}>
                      {dealer.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className='grid grid-cols-3 gap-3'>
            <div className='space-y-1'>
              <Label>유종</Label>
              <Select value={productCode} onValueChange={setProductCode} disabled={orgCd === ''}>
                <SelectTrigger>
                  <SelectValue placeholder={orgCd === '' ? '사업장을 먼저 고르세요' : '유종 선택'} />
                </SelectTrigger>
                <SelectContent>
                  {oils.map(oil => (
                    <SelectItem key={oil.code} value={oil.code}>
                      {oil.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className='space-y-1'>
              <Label>판매구분</Label>
              <Select value={oilSaleType} onValueChange={setOilSaleType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {OIL_SALE_TYPES.map(t => (
                    <SelectItem key={t.code} value={t.code}>
                      {t.code}. {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className='space-y-1'>
              <Label>적용구분</Label>
              <Select value={applyType} onValueChange={setApplyType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {APPLY_TYPES.map(t => (
                    <SelectItem key={t.code} value={t.code}>
                      {t.code}. {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* 지금 유효한 계약. 고를 것이 아니라 보고 판단하는 값이라 읽기 전용이다 */}
          <div className='grid grid-cols-4 gap-3 rounded-md border bg-muted/40 px-3 py-2 text-sm'>
            <div>
              <div className='text-xs text-muted-foreground'>현재 적용일</div>
              <div className='tabular-nums'>{current?.applyDate ?? '-'}</div>
            </div>
            <div>
              <div className='text-xs text-muted-foreground'>현재 적용구분</div>
              <div>{APPLY_TYPES.find(t => t.code === current?.applyType)?.name ?? '-'}</div>
            </div>
            <div>
              <div className='text-xs text-muted-foreground'>현재 단가적용방법</div>
              <div>{PRICE_APP_METHODS.find(t => t.code === current?.priceAppMethod)?.name ?? '-'}</div>
            </div>
            <div>
              <div className='text-xs text-muted-foreground'>현재 계약단가</div>
              <div className={`font-medium tabular-nums ${currentFailed ? 'text-destructive' : ''}`}>
                {currentFailed ? '조회 실패' : current ? contractPriceLabel(current.price, current.priceAppMethod) : '신규'}
              </div>
            </div>
          </div>
          {currentFailed && (
            <p className='text-xs text-destructive'>
              현재 계약단가를 읽지 못했습니다. 결재자가 현재 계약과 비교할 수 없어 상신을 막습니다 - 사업장·거래처·유종을 다시 선택해 주세요
            </p>
          )}

          <div className='grid grid-cols-2 gap-3'>
            <div className='space-y-1'>
              <Label>단가적용방법</Label>
              <Select value={priceAppMethod} onValueChange={setPriceAppMethod}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRICE_APP_METHODS.map(t => (
                    <SelectItem key={t.code} value={t.code}>
                      {t.code}. {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className='space-y-1'>
              <Label htmlFor='dp-date'>적용일</Label>
              <Input id='dp-date' type='date' value={applyDate} onChange={e => setApplyDate(e.target.value)} />
            </div>
          </div>

          <div className='space-y-1'>
            <Label htmlFor='dp-price'>요청단가</Label>
            <Input id='dp-price' type='number' value={requestedPrice} onChange={e => setRequestedPrice(e.target.value)} />
          </div>

          <div className='space-y-1'>
            <Label htmlFor='dp-reason'>승인요청사유</Label>
            <Textarea id='dp-reason' rows={3} value={requestReason} onChange={e => setRequestReason(e.target.value)} maxLength={500} />
          </div>

          <LinePreview body={ready && !currentFailed ? body() : null} preview={preview} />
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)} disabled={saving}>
            취소
          </Button>
          <Button onClick={handleSubmit} disabled={saving || blocked}>
            {saving ? '상신 중...' : '상신'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
